import {
  bookCreationProfileForTemplate,
  getTemplate,
  type CreationProductionFamily,
  type TemplateId,
} from "@lifescapsule/contracts";
import { Link, useParams } from "react-router-dom";
import { BookMockup } from "../../shared/BookMockup";
import { Icon } from "../../shared/Icon";
import { CustomerLinkButton } from "../../shared/CustomerControls";
import NotFoundPage from "../marketing/NotFoundPage";
import { CatalogFixture } from "./CatalogFixture";
import { hasCatalogFixture } from "./catalogFixtureData";
import { FinishedBookPreview } from "./FinishedBookPreview";
import {
  availabilityPresentation,
  catalogAction,
  catalogAvailability,
  catalogOfferPresentation,
  catalogPrintDescription,
  editionFamily,
  familyLabels,
} from "./catalogPresentation";
import { getFinishedBookExample } from "./examples";
import "./catalog-explorer.css";
import "./deal-catalog.css";

const familyJourneyCopy: Record<
  CreationProductionFamily,
  {
    promptTitle: string;
    promptDescription: string;
    sampleTitle: string;
    sampleUnit: string;
  }
> = {
  NARRATIVE: {
    promptTitle: "Questions specific to this edition.",
    promptDescription:
      "Answer all of them, skip any, or add a question your family already knows it wants to ask.",
    sampleTitle: "See how a memory becomes a chapter.",
    sampleUnit: "Sample chapter",
  },
  PICTURE_BOOK: {
    promptTitle: "Prompts for this read-aloud.",
    promptDescription:
      "Choose a memory, decide how the story should feel and imagine the pictures you’d like readers to see.",
    sampleTitle: "See how family direction becomes a page turn.",
    sampleUnit: "Sample page turn",
  },
  RECIPE: {
    promptTitle: "Prompts for each dish.",
    promptDescription:
      "Capture the ingredients, method, substitutions, serving ritual, and family history that belong together.",
    sampleTitle: "See how kitchen knowledge becomes a recipe spread.",
    sampleUnit: "Sample recipe",
  },
  COMIC_CORE: {
    promptTitle: "Prompts for visual storytelling.",
    promptDescription:
      "Choose a real scene and shape its characters, comic panels, captions and dialogue before reviewing the pictures.",
    sampleTitle: "See how a true scene becomes a comic spread.",
    sampleUnit: "Sample scene",
  },
};

/**
 * The guided Bedtime book as buyers actually make it: a few typed sentences, about half an hour,
 * twelve illustrated spreads they approve one by one (end-to-end audit, 2026-10-14). The generic
 * facts describe the memoir's recordings and family review, which Bedtime doesn't use.
 */
const guidedFacts: Partial<
  Record<TemplateId, { gather: [string, string]; review: [string, string]; inside: string }>
> = {
  children: {
    gather: ["How you tell it", "A few typed sentences"],
    review: ["Before anything is final", "You approve every page"],
    inside: "12 illustrated spreads",
  },
};

export default function ProductPage() {
  const { templateId = "" } = useParams();
  const template = getTemplate(templateId);
  if (!template) return <NotFoundPage />;
  const example = getFinishedBookExample(template.id);
  const family = editionFamily(template.id);
  const profile = bookCreationProfileForTemplate(template.id);
  const journeyCopy = familyJourneyCopy[profile?.productionFamily ?? "NARRATIVE"];
  const availability = catalogAvailability(template.id);
  const availabilityCopy = availabilityPresentation[availability];
  const action = catalogAction(template.id as TemplateId);
  const offer = catalogOfferPresentation(template.id);
  const isWorking = availability === "WORKING";
  const isSandbox = availability === "SANDBOX";
  const hasFixture = hasCatalogFixture(template.id as TemplateId);
  const guided = guidedFacts[template.id as TemplateId];

  return (
    <main id="main-content" className="customer-design-system deal-product product-page">
      <div className="shell product-breadcrumbs">
        <Link to="/books">The collection</Link>
        <span aria-hidden="true">/</span>
        <span>{familyLabels[family]}</span>
      </div>
      <section
        className={`product-intro product-intro--${family}`}
        style={
          {
            "--product-color": template.palette.cover,
            "--product-ink": template.palette.coverText,
            "--product-accent": template.palette.accent,
          } as React.CSSProperties
        }
      >
        <div className="shell product-intro__grid">
          <div className="product-gallery">
            <div className="product-gallery__main">
              <CatalogFixture
                templateId={template.id as TemplateId}
                className="product-gallery__fixture"
                loading="eager"
                fetchPriority="high"
                fallback={<BookMockup template={template} name="Your storyteller" />}
              />
              <span>
                {hasFixture
                  ? "Fictional sample book"
                  : "Edition cover preview · final design may vary"}
              </span>
            </div>
            <div className="product-gallery__details" role="list" aria-label="Edition facts">
              <div role="listitem">
                <Icon name="book" size={21} />
                <small>Print style</small>
                <strong>{catalogPrintDescription[template.printFormat]}</strong>
              </div>
              <div role="listitem">
                <Icon name="mic" size={21} />
                <small>{guided?.gather[0] ?? "Gather naturally"}</small>
                <strong>{guided?.gather[1] ?? "Voice, text & media"}</strong>
              </div>
              <div role="listitem">
                <Icon name="circleCheck" size={21} />
                <small>{guided?.review[0] ?? "Before anything is final"}</small>
                <strong>{guided?.review[1] ?? "Family review"}</strong>
              </div>
            </div>
          </div>
          <div className="product-summary">
            <h1>{template.name}</h1>
            <p className="product-summary__gift-line">{template.giftLine}</p>
            <p>
              {template.voiceLabel}.{" "}
              {isWorking
                ? "Create your private book, then review the words and pictures before approving it."
                : isSandbox
                  ? "Follow the guided steps to make your private book, then review every page before anything is printed."
                  : "We’re preparing this book’s questions, page designs and review steps. Creation is not open yet."}
            </p>
            <div className="product-readiness-note" role="note">
              <span
                className={`catalog-readiness catalog-readiness--${availability.toLowerCase()}`}
              >
                <i aria-hidden="true" />
                {availabilityCopy.shortLabel}
              </span>
              <p>
                {isSandbox
                  ? "Make a private draft at your own pace. You’ll review the finished files and confirm delivery before a printed copy can be ordered."
                  : availabilityCopy.explanation}
              </p>
            </div>
            <dl className="product-summary__facts">
              <div>
                <dt>Inside your book</dt>
                <dd>
                  {guided?.inside ?? `${profile?.itemLabelPlural ?? "chapters"} with family review`}
                </dd>
              </div>
              <div>
                <dt>Typical pace</dt>
                <dd>{example.commitment}</dd>
              </div>
              <div>
                <dt>Print</dt>
                <dd>{catalogPrintDescription[template.printFormat]}</dd>
              </div>
            </dl>
            <div className="purchase-summary">
              <div>
                <strong>{offer.value}</strong>
                <span>
                  {offer.label}
                  <br />
                  {offer.description}
                </span>
              </div>
              <Link className="button button--coral" to={action.route}>
                {action.label} <Icon name="arrow" />
              </Link>
              <p className="purchase-summary__boundary">{action.note}</p>
              <div className="deal-product__supporting-links">
                <CustomerLinkButton
                  tone="quiet"
                  to="#example"
                  iconAfter={<Icon name="arrow" size={16} />}
                >
                  See the fictional book sample
                </CustomerLinkButton>
                <CustomerLinkButton tone="quiet" to="/pricing">
                  See voucher details
                </CustomerLinkButton>
                <CustomerLinkButton tone="quiet" to="/redeem">
                  Redeem a voucher
                </CustomerLinkButton>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section product-prompts" aria-labelledby="product-prompts-title">
        <div className="shell product-prompts__grid">
          <header>
            <h2 id="product-prompts-title">{journeyCopy.promptTitle}</h2>
            <p>{journeyCopy.promptDescription}</p>
          </header>
          <ol>
            {template.prompts.slice(0, 2).map((prompt, index) => (
              <li key={prompt}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{prompt}</p>
              </li>
            ))}
          </ol>
          {template.prompts.length > 2 && (
            <details className="product-prompts__more">
              <summary>See four more sample prompts</summary>
              <ol start={3}>
                {template.prompts.slice(2, 6).map((prompt, index) => (
                  <li key={prompt}>
                    <span>{String(index + 3).padStart(2, "0")}</span>
                    <p>{prompt}</p>
                  </li>
                ))}
              </ol>
            </details>
          )}
        </div>
      </section>

      <section
        className="product-example section"
        id="example"
        aria-labelledby="product-example-title"
      >
        <div className="shell">
          <header className="product-example__heading">
            <div>
              <h2 id="product-example-title">{journeyCopy.sampleTitle}</h2>
            </div>
            <div className="product-example__facts">
              <p>
                <small>BEST FOR</small>
                <strong>{example.bestFor}</strong>
              </p>
              <p>
                <small>{journeyCopy.sampleUnit}</small>
                <strong>{example.chapter}</strong>
              </p>
            </div>
          </header>
          <FinishedBookPreview template={template} />
          <p className="example-disclaimer">
            Fictional interface sample. It is not customer content, a physical-product photograph,
            or a working QR destination.
          </p>
        </div>
      </section>

      <section className="section product-specs" aria-labelledby="product-specs-title">
        <div className="shell product-specs__grid">
          <div>
            <h2 id="product-specs-title">
              {availability === "ROADMAP"
                ? "What this edition is being designed to become."
                : "What you can add, edit and review."}
            </h2>
          </div>
          <details className="product-specs__details">
            <summary>What you can add, change and review</summary>
            <dl>
              <div>
                <dt>Responses</dt>
                <dd>
                  {profile?.capturePriorities
                    .map((capture) => capture.mode.toLowerCase())
                    .join(", ")}
                </dd>
              </div>
              <div>
                <dt>Editing</dt>
                <dd>
                  Compare the draft with your memories, edit it, revisit earlier versions and
                  approve the words
                </dd>
              </div>
              <div>
                <dt>Privacy</dt>
                <dd>Private to the people who have permission to read or work on the book</dd>
              </div>
              <div>
                <dt>Print files</dt>
                <dd>
                  {availability === "ROADMAP"
                    ? "Proof controls are being prepared; ordering is not available"
                    : "Cover and interior review before approval"}
                </dd>
              </div>
              <div>
                <dt>Physical copy</dt>
                <dd>
                  {catalogPrintDescription[template.printFormat]}. Size and ordering details are
                  confirmed with your print proof.
                </dd>
              </div>
              <div>
                <dt>Delivery</dt>
                <dd>Shipment begins only after an order is explicitly confirmed</dd>
              </div>
            </dl>
          </details>
        </div>
      </section>

      <section className="section product-faq" aria-labelledby="product-faq-title">
        <div className="shell product-faq__grid">
          <h2 id="product-faq-title">Before you choose {template.shortName}</h2>
          <div>
            <details>
              <summary>How are prompts used?</summary>
              <p>
                {isWorking
                  ? `The ${template.prompts.length}-question path is a guide, not a requirement. The storyteller can skip, reorder, or add questions.`
                  : isSandbox
                    ? `The guided experience uses ${profile?.itemLabelPlural ?? "parts of the book"}, questions for this kind of story and clear steps for your review.`
                    : `This edition is planned around ${template.prompts.length} questions. You can explore its example, but can’t start your own book yet.`}
              </p>
            </details>
            <details>
              <summary>Who controls the final wording?</summary>
              <p>
                {isWorking
                  ? "You do. Compare the draft with your original memories, make changes and revisit earlier versions. The book owner or an editor approves the private preview."
                  : isSandbox
                    ? "You do. Keep earlier drafts, edit the words and follow this book’s review steps before approving it."
                    : "The planned steps explain who reviews the book. You can explore them, but can’t create or approve a book in this edition yet."}
              </p>
            </details>
            <details>
              <summary>Can I order a printed copy today?</summary>
              <p>
                {offer.voucherEligible
                  ? "The voucher includes one printed copy and standard US shipping. Delivery becomes available only after your print files are approved and the book passes fulfillment checks. Choosing an edition or opening a private workspace does not place an order."
                  : "This edition is not included in the current book voucher. Private creation is available, but printed ordering has not opened for this edition."}
              </p>
            </details>
          </div>
        </div>
      </section>

      <div className="mobile-purchase-bar" role="region" aria-label="Book offer and next step">
        <span>
          <strong>{offer.value}</strong>
          <small>{offer.label}</small>
        </span>
        <Link className="button button--coral" to={action.route}>
          {action.label}
        </Link>
      </div>
    </main>
  );
}
